import axios from 'axios'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { LocalFind, LocalInsert } from '@weblite/persist'
import hostConf from '$config/frontend'
import { sendFcmTokenToServer } from '$logic/push/service'
import { handleLink } from '$logic/router/url'
import { loginUserFromAndroid } from '$logic/auth'
import { isInTwa } from '$func/environmentInfo'
import { TWA_API_SUPPORT } from '$logic/twaCommunication/service'
import { callService } from '$logic/call'
import { SESSION_STORAGE_KEYS } from '$logic/client-device/types'
import { TWA_EVENTS } from './twaCommunication.types'
import { getPort, getWsUrl } from './twaCommiunication.state'
import { sessionStorageSetItem } from '$func/session-storage'

const setTokenViaHttp = async (token: string) => {
  const port = getPort()
  if (!port) throw Error('no-port')
  await axios.get(`http://127.0.0.1:${port}/set-option?auth_token=${token}`)
}

const handleFirebaseToken = (token: string) => {
  const oldToken = LocalFind('twaFcmRegistrationId')
  if (token !== oldToken) {
    sendFcmTokenToServer(token)
    LocalInsert('twaFcmRegistrationId', token)
  }
}

const handleAuth = (token: string) => {
  const currentToken = LocalFind('auth')?.token

  if (!currentToken && token) {
    loginUserFromAndroid(token)
  }
}

const handleMessage = (event: MessageEvent) => {
  // alert(`New TWA event: '${event.data}'`)
  const [type, payloadText] = event.data.split('/', 2)
  let payload
  try {
    payload = JSON.parse(payloadText)
    if (!payload) return
  } catch (e) {
    return
  }
  switch (type) {
    case 'FirebaseToken':
      handleFirebaseToken(payload.token)
      break
    case 'HandleLink':
      handleLink(new URL(payload.link, hostConf.FRONT_HOST).href)
      break
    case 'HandleNotificationPermission':
      break
    case 'HandleContactPermission':
      if (payload.contact_perm === 'allow')
        sendEventToTwa(TWA_EVENTS.SYNC_CONTACT)
      sessionStorageSetItem(
        SESSION_STORAGE_KEYS.CONTACT_PERM,
        payload.contact_perm,
      )
      break
    case 'AuthInfo':
      if (payload.token) handleAuth(payload.token)
      break
    case 'Pong':
      handlePong()
      break
    case 'acceptIncomingCall':
      callService.acceptIncomingCallByRoomId(payload.roomId)
      break
    case 'rejectIncomingCall':
      callService.rejectIncomingCallByRoomId(payload.roomId)
      break
    case 'endOnGoingCall':
      callService.endOngoingCallByRoomId(payload.roomId)
      break
    default:
      break
  }
}

let wsPromise: Promise<WebSocket> | undefined
const createWebSocket = () => {
  const wsUrl = getWsUrl()
  if (!wsUrl) throw Error('no-ws-url')

  const socket = new WebSocket(wsUrl)
  return new Promise((resolve, reject) => {
    socket.addEventListener('open', resolve)
    socket.addEventListener('error', reject, { once: true })
    socket.addEventListener(
      'error',
      console.log.bind(null, 'TWA WebSocket error:'),
    )
    socket.addEventListener('message', handleMessage)
  }).then(() => socket)
}

const createWebsocketForConnection = async () => {
  if (!getWsUrl()) return undefined
  if (!wsPromise) wsPromise = createWebSocket()
  try {
    const ws = await wsPromise
    if (
      ws.readyState === WebSocket.CLOSED ||
      ws.readyState === WebSocket.CLOSING
    ) {
      wsPromise = undefined
      return undefined
    }
    return ws
  } catch (e) {
    wsPromise = undefined
    return undefined
  }
}

export const sendEventToTwa = async (
  type: TWA_EVENTS,
  payload?: any,
  retry = true,
) => {
  const ws = await createWebsocketForConnection()

  if (ws) ws.send(`${type}/${JSON.stringify(payload)}`)
  else if (retry) sendEventToTwa(type, payload, false)
}

let ackTimeout: NodeJS.Timeout
const handlePong = () => {
  clearTimeout(ackTimeout)
}

const PONG_TIMEOUT = 5000
const checkConnection = () => {
  sendEventToTwa(TWA_EVENTS.PING)
  ackTimeout = setTimeout(() => {
    wsPromise = undefined
    wsPromise = createWebSocket()
  }, PONG_TIMEOUT)
}
const RECHECK_TIMEOUT = 10000
export const checkConnectionWithTWA = () => {
  if (!isInTwa || !TWA_API_SUPPORT.RECONNECT_WEBSOCKET) return
  setInterval(() => {
    checkConnection()
  }, RECHECK_TIMEOUT)
}

export const sendBinaryToTwa = async (
  payload: Parameters<WebSocket['send']>[0],
) => {
  const ws = await createWebsocketForConnection()
  if (ws) ws.send(payload)
}

export const sendAuthTokenToTwa = async (token: string) =>
  getWsUrl()
    ? sendEventToTwa(TWA_EVENTS.AUTH_TOKEN, { token })
    : setTokenViaHttp(token)
